import * as d3 from "d3"
import { parseCoordinate } from "../../helpers/chart_helpers"

export default class WeatherLayerDrawer {
    static HIGHLIGHT_OPTIONS = {
        background: "background",
        backgroundHover: "background_hover",
        bar: "bar",
        line: "line",
        point: "point"
    }

    static STACKED_ASTRO_ANIMATION = "stacked_over"

    static LAYER_SIZE = {
        day: 64,
        hour: 62,
        astro: 62
    }

    static MARKER_WIDTH = {
        day: 104,
        hour: 48,
        astro: 48
    }

    static RIGHT_OVERFLOW = 84

    static ICON_CACHE = new Map()

    static loadIcon(url) {
        if (!this.ICON_CACHE.has(url)) {
            this.ICON_CACHE.set(url, fetch(url)
                .then(response => {
                    if (!response.ok) throw new Error(`Unable to load icon: ${url}`)
                    return response.text()
                })
                .then(svg => new DOMParser().parseFromString(svg, "image/svg+xml"))
            )
        }

        return this.ICON_CACHE.get(url)
    }

    static getSizeAndInstance(layers, { xDomain = null, xScaleType = null } = {}) {
        const weatherLayers = layers.filter(layer => (
            layer.renderer === "weather" &&
            layer.enabled_by_default !== false &&
            this.hasRenderableData(layer, { xDomain, xScaleType })
        ))
        const size = weatherLayers.reduce((acc, layer) => acc + this.layerHeightFor(layer), 0)
        const aboveSize = weatherLayers
            .filter(layer => layer.position === "above")
            .reduce((acc, layer) => acc + this.layerHeightFor(layer), 0)

        return { size, aboveSize, drawer: new WeatherLayerDrawer(weatherLayers) }
    }

    static layerHeightFor(layer) {
        return this.isStackedAstroLayer(layer) ? 0 : (this.LAYER_SIZE[layer.options?.type] || 0)
    }

    static minimumWidthFor(layers) {
        const gap = 8
        const weatherLayers = layers.filter(layer => (
            layer.renderer === "weather" &&
            layer.enabled_by_default !== false &&
            layer.options?.type !== "astro"
        ))

        const markerWidth = weatherLayers.reduce((width, layer) => {
            const layerMarkerWidth = this.MARKER_WIDTH[layer.options?.type] || this.MARKER_WIDTH.hour
            const markerCount = layer.data.length
            if (markerCount === 0) return width

            return Math.max(width, (markerCount * layerMarkerWidth) + ((markerCount - 1) * gap))
        }, 0)

        return markerWidth + this.RIGHT_OVERFLOW
    }

    static isStackedAstroLayer(layer) {
        return (
            layer?.options?.type === "astro" &&
            layer.options?.animation === this.STACKED_ASTRO_ANIMATION
        )
    }

    static hasRenderableData(layer, { xDomain = null, xScaleType = null } = {}) {
        const data = layer.data || []
        return data.length > 0
    }

    static isAstroTimeInDomain(time, xDomain, xScaleType) {
        const value = parseCoordinate(time, xScaleType)

        return value >= xDomain[0] && value <= xDomain[1]
    }

    constructor(layers) {
        this.layers = layers;
        this.timezone = layers.find(layer => layer.options?.timezone)?.options.timezone
        this.hoverTargets = []
        this.expandedStack = null
    }

    async draw({ svg, xAxis, layersAboveSize, margin, innerWidth, innerHeight, dataXPositions = [] }) {
        if (this.layers.length === 0) return

        this.hoverTargets = []
        this.dataXPositions = dataXPositions
        this.xDomain = xAxis.scale.domain()
        this.xScaleType = xAxis.scaleType
        await this.drawPositionedLayers({
            svg,
            xAxis,
            layers: this.layers.filter(layer => layer.position === "above"),
            startY: 0,
            layersAboveSize,
            margin,
            innerWidth,
            innerHeight
        })
        await this.drawPositionedLayers({
            svg,
            xAxis,
            layers: this.layers.filter(layer => ["below_axis", "below_axis_label", "below"].includes(layer.position)),
            startY: layersAboveSize + margin.top + innerHeight + 8,
            layersAboveSize,
            margin,
            innerWidth,
            innerHeight
        })
    }

    async drawPositionedLayers({ svg, xAxis, layers, startY, layersAboveSize, margin, innerWidth, innerHeight }) {
        let y = startY
        let pendingStackedAstroLayer = null

        for (let index = 0; index < layers.length; index++) {
            const layer = layers[index]
            const nextLayer = layers[index + 1]
            const nextStackedAstroLayer = WeatherLayerDrawer.isStackedAstroLayer(nextLayer) ? nextLayer : null

            if (WeatherLayerDrawer.isStackedAstroLayer(layer)) {
                pendingStackedAstroLayer = layer
                continue
            }

            const layerHeight = WeatherLayerDrawer.layerHeightFor(layer)
            const stackedAstroLayer = pendingStackedAstroLayer || nextStackedAstroLayer
            if (layer.options?.type === "day") await this.drawDayLayer({ svg, xAxis, layer, y, layerHeight, margin, layersAboveSize, innerWidth, innerHeight, stackedAstroLayer })
            if (layer.options?.type === "hour") await this.drawHourLayer({ svg, xAxis, layer, y, layerHeight, margin, layersAboveSize, innerWidth, innerHeight, stackedAstroLayer })
            if (layer.options?.type === "astro") await this.drawAstroLayer({ svg, xAxis, layer, y, layerHeight, margin, layersAboveSize, innerWidth, innerHeight })

            if (nextStackedAstroLayer) index++
            pendingStackedAstroLayer = null
            y += layerHeight
        }
    }

    async drawDayLayer({ svg, xAxis, layer, y, layerHeight, margin, layersAboveSize, innerWidth, innerHeight, stackedAstroLayer = null }) {
        const rowY = y + 28
        const iconSize = layer.options?.icon_size || 28
        const iconColor = layer.options?.icon_color || "#f59e0b"
        const maxTempColor = layer.options?.max_temp_color || "#111827"
        const minTempColor = layer.options?.min_temp_color || "#6b7280"
        const highlight = layer.options?.highlight
        const highlightColor = layer.options?.highlight_color
        const chartArea = {
            x: margin.left,
            y: layersAboveSize + margin.top,
            width: innerWidth,
            height: innerHeight
        }

        const group = svg
            .append("g")
            .attr("class", "weather-layer weather-layer-day")

        this.drawHighlight({ svg, layer, xAxis, margin, chartArea, rowY, layerHeight, highlight, highlightColor })

        const stackedAstroMarkers = await this.drawStackedAstroLayer({
            svg,
            group,
            astroLayer: stackedAstroLayer,
            weatherLayer: layer,
            weatherType: "day",
            xAxis,
            margin,
            rowY,
            layersAboveSize,
            innerWidth,
            innerHeight
        })

        const [domainStart, domainEnd] = xAxis.scale.domain()
        const visibleData = (layer.data || []).filter(row => {
            const startsAt = parseCoordinate(this.startTimeFor(row) ?? this.timeFor(row), xAxis.scaleType)
            const endsAt = parseCoordinate(this.endTimeFor(row) ?? this.timeFor(row), xAxis.scaleType)
            return endsAt >= domainStart && startsAt <= domainEnd
        })

        const markers = group
            .selectAll(".weather-layer-day-marker")
            .data(visibleData)
            .join("g")
            .attr("class", "weather-layer-day-marker")
            .attr("transform", row => {
                // Keep the icon and temperature badge visible when a day is only partly plotted.
                const x = Math.max(margin.left + 24, Math.min(this.xPositionFor(row, xAxis, margin), margin.left + innerWidth - 80))
                return `translate(${x}, ${rowY})`
            })
            .attr("color", iconColor)
            .attr("data-tooltip-target", "tooltip")
            .attr("data-tooltip-text", row => this.titleFor(row))
            .attr("pointer-events", "all")

        markers
            .insert("rect", ":first-child")
            .attr("class", "weather-layer-hitbox")
            .attr("x", -24)
            .attr("y", -26)
            .attr("width", 104)
            .attr("height", 56)
            .attr("fill", "transparent")

        markers
            .append("circle")
            .attr("class", "weather-layer-icon-halo")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", (iconSize / 2) + 5)
            .attr("fill", "#fff7ed")
            .attr("stroke", "#fed7aa")
            .attr("stroke-width", 1)

        markers
            .append("rect")
            .attr("class", "weather-layer-day-temp-badge")
            .attr("x", 15)
            .attr("y", -20)
            .attr("width", 62)
            .attr("height", 40)
            .attr("rx", 10)
            .attr("fill", "#ffffff")
            .attr("stroke", "#e2e8f0")
            .attr("stroke-width", 1)
            .attr("filter", "drop-shadow(0 1px 3px rgba(15, 23, 42, 0.16))")

        markers
            .append("text")
            .attr("class", "weather-layer-max-temp")
            .attr("x", 21)
            .attr("y", -5)
            .attr("font-size", 14)
            .attr("font-weight", 700)
            .attr("fill", maxTempColor)
            .attr("dominant-baseline", "middle")
            .text(row => `${this.maxTempFor(row)}°C`)

        markers
            .append("text")
            .attr("class", "weather-layer-min-temp")
            .attr("x", 21)
            .attr("y", 13)
            .attr("font-size", 11)
            .attr("font-weight", 600)
            .attr("fill", minTempColor)
            .attr("dominant-baseline", "middle")
            .text(row => `${this.minTempFor(row)}°C`)

        await Promise.all(markers.nodes().map((node, index) => (
            this.drawInlineIcon({ node, url: this.iconUrlFor(visibleData[index]), iconSize, className: "weather-layer-day-icon" })
        )))

        this.bindStackedAstroHover({
            markers,
            stackedAstroMarkers,
            keyForDatum: row => this.dayStackKeyFor(row),
            hoverZone: { x: -42, y: -34, width: 138, height: 76 }
        })
    }

    async drawHourLayer({ svg, xAxis, layer, y, layerHeight, margin, layersAboveSize, innerWidth, innerHeight, stackedAstroLayer = null }) {
        const rowY = y + 25
        const iconSize = layer.options?.icon_size || 32
        const iconColor = layer.options?.icon_color || "#64748b"
        const markerData = this.hourMarkerDataFor(layer.data || [], xAxis, margin, layer.options)

        const group = svg
            .append("g")
            .attr("class", "weather-layer weather-layer-hour")

        const stackedAstroMarkers = await this.drawStackedAstroLayer({
            svg,
            group,
            astroLayer: stackedAstroLayer,
            weatherLayer: layer,
            weatherType: "hour",
            xAxis,
            margin,
            rowY,
            layersAboveSize,
            innerWidth,
            innerHeight
        })

        const markers = group
            .selectAll(".weather-layer-hour-marker")
            .data(markerData)
            .join("g")
            .attr("class", "weather-layer-hour-marker")
            .attr("transform", marker => `translate(${marker.x}, ${rowY})`)
            .attr("color", iconColor)
            .attr("data-tooltip-target", "tooltip")
            .attr("data-tooltip-text", marker => this.hourTitleFor(marker.row))
            .attr("pointer-events", "all")

        markers
            .insert("rect", ":first-child")
            .attr("class", "weather-layer-hitbox")
            .attr("x", -24)
            .attr("y", -25)
            .attr("width", 48)
            .attr("height", 62)
            .attr("fill", "transparent")

        markers
            .append("circle")
            .attr("class", "weather-layer-icon-halo")
            .attr("cx", 0)
            .attr("cy", -1)
            .attr("r", (iconSize / 2) + 5)
            .attr("fill", "#f8fafc")
            .attr("stroke", "#e2e8f0")
            .attr("stroke-width", 1)

        markers
            .append("rect")
            .attr("class", "weather-layer-hour-temp-badge")
            .attr("x", -17)
            .attr("y", 16)
            .attr("width", 34)
            .attr("height", 18)
            .attr("rx", 9)
            .attr("fill", "#ffffff")
            .attr("stroke", "#cbd5e1")
            .attr("stroke-width", 1)
            .attr("filter", "drop-shadow(0 1px 2px rgba(15, 23, 42, 0.18))")

        markers
            .append("text")
            .attr("class", "weather-layer-hour-temp")
            .attr("x", 0)
            .attr("y", 25)
            .attr("font-size", 11)
            .attr("font-weight", 700)
            .attr("fill", layer.options?.temperature_color || "#334155")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(marker => `${this.temperatureFor(marker.row)}°C`)

        await Promise.all(markers.nodes().map((node, index) => (
            this.drawInlineIcon({
                node,
                url: this.hourIconUrlFor(markerData[index].row),
                iconSize,
                className: "weather-layer-hour-icon"
            })
        )))

        this.bindStackedAstroHover({
            markers,
            stackedAstroMarkers,
            keyForDatum: marker => this.hourStackKeyFor(marker),
            hoverZone: { x: -36, y: -31, width: 72, height: 74 }
        })
    }

    hourMarkerDataFor(data, xAxis, margin, options = {}) {
        const markersByX = new Map()

        data.forEach(row => {
            const rawX = this.xPositionFor(row, xAxis, margin)
            const x = options.snap_to_data === false ? rawX : this.nearestDataXPosition(rawX)
            const key = Math.round(x)
            const existing = markersByX.get(key)
            const marker = { row, x, distance: Math.abs(rawX - x) }

            if (!existing || marker.distance < existing.distance) markersByX.set(key, marker)
        })

        return Array.from(markersByX.values()).sort((a, b) => a.x - b.x)
    }

    async drawStackedAstroLayer({ svg, group, astroLayer, weatherLayer, weatherType, xAxis, margin, rowY, layersAboveSize, innerWidth, innerHeight }) {
        if (!WeatherLayerDrawer.isStackedAstroLayer(astroLayer)) return null

        const iconSize = astroLayer.options?.icon_size || 32
        const markerData = this.stackedAstroMarkerDataFor({
            astroLayer,
            weatherLayer,
            weatherType,
            xAxis,
            margin,
            rowY
        })

        this.drawAstroHighlight({
            svg,
            layer: {
                ...astroLayer,
                data: astroLayer.data || []
            },
            xAxis,
            margin,
            chartArea: {
                x: margin.left,
                y: layersAboveSize + margin.top,
                width: innerWidth,
                height: innerHeight
            }
        })

        if (markerData.length === 0) return null

        const markers = group
            .selectAll(".weather-layer-astro-stacked-marker")
            .data(markerData)
            .join("g")
            .attr("class", row => `weather-layer-astro-stacked weather-layer-astro-stacked-${astroLayer.options.animation} weather-layer-astro-stacked-marker weather-layer-astro-${row.type}`)
            .attr("transform", row => this.stackedAstroTransform(row))
            .attr("color", row => this.astroColorFor(row.type))
            .attr("opacity", 1)
            .attr("data-tooltip-target", "tooltip")
            .attr("data-tooltip-text", row => this.astroTitleFor(row))
            .attr("pointer-events", "none")

        this.drawAstroMarkerShell(markers, iconSize, { showTime: false })

        await Promise.all(markers.nodes().map((node, index) => (
            this.drawInlineIcon({
                node,
                url: this.astroIconUrlFor(markerData[index]),
                iconSize,
                className: "weather-layer-astro-icon"
            })
        )))

        return markers
    }

    drawAstroMarkerShell(markers, iconSize, { showTime = true } = {}) {
        const timeOpacity = showTime ? 1 : 0

        markers
            .insert("rect", ":first-child")
            .attr("class", "weather-layer-hitbox")
            .attr("x", -24)
            .attr("y", -25)
            .attr("width", 48)
            .attr("height", 62)
            .attr("fill", "transparent")

        markers
            .append("circle")
            .attr("class", "weather-layer-icon-halo")
            .attr("cx", 0)
            .attr("cy", -1)
            .attr("r", (iconSize / 2) + 5)
            .attr("fill", row => row.type === "sunrise" ? "#fff7ed" : "#f5f3ff")
            .attr("stroke", row => row.type === "sunrise" ? "#fed7aa" : "#ddd6fe")
            .attr("stroke-width", 1)

        markers
            .append("rect")
            .attr("class", "weather-layer-astro-time-badge")
            .attr("x", -18)
            .attr("y", 16)
            .attr("width", 36)
            .attr("height", 18)
            .attr("rx", 9)
            .attr("fill", "#ffffff")
            .attr("stroke", "#cbd5e1")
            .attr("stroke-width", 1)
            .attr("filter", "drop-shadow(0 1px 2px rgba(15, 23, 42, 0.18))")
            .attr("opacity", timeOpacity)

        markers
            .append("text")
            .attr("class", "weather-layer-astro-time")
            .attr("x", 0)
            .attr("y", 25)
            .attr("font-size", 10)
            .attr("font-weight", 700)
            .attr("fill", "#334155")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("opacity", timeOpacity)
            .text(row => this.timeLabelFor(row.time))
    }

    stackedAstroMarkerDataFor({ astroLayer, weatherLayer, weatherType, xAxis, margin, rowY }) {
        return (astroLayer.data || []).flatMap(row => {
            const markers = this.visibleAstroMarkers(row)
            if (markers.length === 0) return []

            return markers.map(marker => {
                const rawX = xAxis.scale(parseCoordinate(marker.time, xAxis.scaleType)) + margin.left
                let stackKey

                if (weatherType === "day") {
                    const weatherRow = this.weatherDayForAstro(marker.time, weatherLayer.data || [])
                    if (!weatherRow) return null

                    stackKey = this.dayStackKeyFor(weatherRow)
                } else {
                    const weatherPositions = weatherLayer.options?.snap_to_data === false
                        ? (weatherLayer.data || []).map(row => this.xPositionFor(row, xAxis, margin))
                        : this.dataXPositions
                    stackKey = this.hourStackKeyFor({ x: this.nearestDataXPosition(rawX, weatherPositions) })
                }

                return { ...marker, baseX: rawX, baseY: rowY, stackKey }
            }).filter(Boolean)
        })
    }

    weatherDayForAstro(time, weatherRows) {
        const astroTime = parseCoordinate(time, this.xScaleType)
        const containingRow = weatherRows.find(row => {
            const startsAt = parseCoordinate(this.startTimeFor(row), this.xScaleType)
            const endsAt = parseCoordinate(this.endTimeFor(row), this.xScaleType)

            return startsAt <= astroTime && astroTime <= endsAt
        })
        if (containingRow) return containingRow

        return d3.least(weatherRows, row => Math.abs(parseCoordinate(this.timeFor(row), this.xScaleType) - astroTime))
    }

    bindStackedAstroHover({ markers, stackedAstroMarkers, keyForDatum, hoverZone }) {
        if (!stackedAstroMarkers) return

        const drawer = this
        const hoverZones = markers
            .append("rect")
            .attr("class", "weather-layer-stack-hover-zone")
            .attr("x", hoverZone.x)
            .attr("y", hoverZone.y)
            .attr("width", hoverZone.width)
            .attr("height", hoverZone.height)
            .attr("fill", "transparent")
            .attr("pointer-events", "all")

        hoverZones
            .on("mouseenter.stackedAstro", function (_, row) {
                const stackKey = keyForDatum(row)
                if (!drawer.hasStackedAstroForKey(stackedAstroMarkers, stackKey)) return

                const marker = d3.select(this.parentNode)
                drawer.expandStackedAstroMarker({ stackedAstroMarkers, stackKey, marker })
            })
            .on("mouseleave.stackedAstro pointerleave.stackedAstro", function (_, row) {
                const stackKey = keyForDatum(row)
                if (!drawer.hasStackedAstroForKey(stackedAstroMarkers, stackKey)) return

                drawer.resetExpandedStack()
            })

        const stackedAstroHoverZones = d3.select(stackedAstroMarkers.node().parentNode)
            .selectAll(".weather-layer-astro-stack-hover-zone")
            .data(stackedAstroMarkers.data())
            .join("rect")
            .attr("class", "weather-layer-astro-stack-hover-zone")
            .attr("x", row => row.baseX - 24)
            .attr("y", row => row.baseY - 25)
            .attr("width", 48)
            .attr("height", 62)
            .attr("fill", "transparent")
            .attr("pointer-events", "all")

        stackedAstroHoverZones
            .on("mouseenter.stackedAstro", function (_, row) {
                drawer.expandStackedAstroMarker({
                    stackedAstroMarkers,
                    stackKey: row.stackKey,
                    marker: d3.select(this)
                })
            })
            .on("mouseleave.stackedAstro pointerleave.stackedAstro", () => {
                drawer.resetExpandedStack()
            })
    }

    expandStackedAstroMarker({ stackedAstroMarkers, stackKey, marker }) {
        if (
            this.expandedStack?.stackKey === stackKey &&
            this.expandedStack?.stackedAstroMarkers === stackedAstroMarkers
        ) return

        this.resetExpandedStack()
        marker?.raise()
        this.transitionStackedAstro(stackedAstroMarkers, stackKey, true)
        this.expandedStack = {
            stackedAstroMarkers,
            stackKey
        }
    }

    resetExpandedStack() {
        if (!this.expandedStack) return

        this.transitionStackedAstro(
            this.expandedStack.stackedAstroMarkers,
            this.expandedStack.stackKey,
            false,
            70
        )
        this.expandedStack = null
    }

    transitionStackedAstro(markers, stackKey, expanded, duration = null) {
        const activeMarkers = markers.filter(row => row.stackKey === stackKey)

        if (expanded) {
            activeMarkers.raise()
        } else {
            activeMarkers.lower()
        }

        activeMarkers
            .interrupt()
            .transition()
            .duration(duration ?? (expanded ? 120 : 90))
            .ease(d3.easeCubicOut)
            .attr("opacity", 1)
            .attr("pointer-events", "none")
            .attr("transform", row => this.stackedAstroTransform(row))

        activeMarkers
            .selectAll(".weather-layer-astro-time-badge, .weather-layer-astro-time")
            .interrupt()
            .transition()
            .duration(duration ?? (expanded ? 120 : 90))
            .ease(d3.easeCubicOut)
            .attr("opacity", expanded ? 1 : 0)
    }

    stackedAstroTransform(row) {
        return `translate(${row.baseX}, ${row.baseY})`
    }

    hasStackedAstroForKey(markers, stackKey) {
        return markers
            .data()
            .some(astroMarker => astroMarker.stackKey === stackKey)
    }

    dayStackKeyFor(row) {
        return `day-${this.timeFor(row)}`
    }

    hourStackKeyFor(marker) {
        return `hour-${Math.round(marker.x)}`
    }

    async drawAstroLayer({ svg, xAxis, layer, y, layerHeight, margin, layersAboveSize, innerWidth, innerHeight }) {
        const rowY = y + 28
        const iconSize = layer.options?.icon_size || 32
        const markerData = (layer.data || []).flatMap(row => {
            return this.visibleAstroMarkers(row)
        })

        const chartArea = {
            x: margin.left,
            y: layersAboveSize + margin.top,
            width: innerWidth,
            height: innerHeight
        }
        const group = svg
            .append("g")
            .attr("class", "weather-layer weather-layer-astro")

        this.drawAstroHighlight({ svg, layer, xAxis, margin, chartArea })

        if (markerData.length === 0) return

        const markers = group
            .selectAll(".weather-layer-astro-marker")
            .data(markerData)
            .join("g")
            .attr("class", row => `weather-layer-astro-marker weather-layer-astro-${row.type}`)
            .attr("transform", row => `translate(${xAxis.scale(parseCoordinate(row.time, xAxis.scaleType)) + margin.left}, ${rowY})`)
            .attr("color", row => this.astroColorFor(row.type))
            .attr("data-tooltip-target", "tooltip")
            .attr("data-tooltip-text", row => this.astroTitleFor(row))
            .attr("pointer-events", "all")

        this.drawAstroMarkerShell(markers, iconSize)

        await Promise.all(markers.nodes().map((node, index) => (
            this.drawInlineIcon({
                node,
                url: this.astroIconUrlFor(markerData[index]),
                iconSize,
                className: "weather-layer-astro-icon"
            })
        )))
    }

    drawHighlight({ svg, layer, xAxis, margin, chartArea, rowY, layerHeight, highlight, highlightColor }) {
        if (![
            WeatherLayerDrawer.HIGHLIGHT_OPTIONS.background,
            WeatherLayerDrawer.HIGHLIGHT_OPTIONS.backgroundHover,
            WeatherLayerDrawer.HIGHLIGHT_OPTIONS.bar
        ].includes(highlight)) return

        const data = layer.data || []
        const highlightGroup = svg
            .append("g")
            .attr("class", `weather-layer-highlight weather-layer-highlight-${highlight}`)

        if (highlight !== WeatherLayerDrawer.HIGHLIGHT_OPTIONS.bar) {
            this.drawBackgroundHighlight({ svg, group: highlightGroup, layer, data, xAxis, margin, chartArea, highlight, highlightColor })
            highlightGroup.lower()
            return
        }

        const barY = rowY + Math.min(26, layerHeight - rowY - 8)

        highlightGroup
            .selectAll(".weather-layer-day-bar")
            .data(data)
            .join("line")
            .attr("class", "weather-layer-day-bar")
            .attr("x1", (row, index) => this.coverageFor(row, index, data, xAxis, margin).start)
            .attr("x2", (row, index) => this.coverageFor(row, index, data, xAxis, margin).end)
            .attr("y1", barY)
            .attr("y2", barY)
            .attr("stroke", row => this.highlightColorFor(row, layer, data))
            .attr("stroke-width", layer.options?.highlight_width || 4)
            .attr("stroke-linecap", "round")
            .attr("opacity", layer.options?.highlight_opacity ?? 0.85)
    }

    drawBackgroundHighlight({ svg, group, layer, data, xAxis, margin, chartArea, highlight, highlightColor }) {
        const backgroundColor = layer.options?.chart_background_color || "#ffffff"
        const defs = this.defsFor(svg)

        group
            .append("rect")
            .attr("class", "weather-layer-chart-background")
            .attr("x", chartArea.x)
            .attr("y", chartArea.y)
            .attr("width", chartArea.width)
            .attr("height", chartArea.height)
            .attr("fill", backgroundColor)
            .attr("pointer-events", "none")

        group
            .selectAll(".weather-layer-day-background")
            .data(data)
            .join("rect")
            .attr("class", "weather-layer-day-background")
            .attr("x", (row, index) => this.coverageFor(row, index, data, xAxis, margin).start)
            .attr("y", chartArea.y)
            .attr("width", (row, index) => this.coverageFor(row, index, data, xAxis, margin).width)
            .attr("height", chartArea.height)
            .attr("fill", row => `url(#${this.backgroundGradientIdFor({ defs, row, layer, data, backgroundColor, highlightColor })})`)
            .attr("opacity", highlight === WeatherLayerDrawer.HIGHLIGHT_OPTIONS.backgroundHover ? 0 : 1)
            .attr("pointer-events", "none")

        if (highlight === WeatherLayerDrawer.HIGHLIGHT_OPTIONS.backgroundHover) {
            this.hoverTargets.push(...group.selectAll(".weather-layer-day-background").nodes().map((node, index) => ({
                node,
                ...this.coverageFor(data[index], index, data, xAxis, margin)
            })))
        }
    }

    drawAstroHighlight({ svg, layer, xAxis, margin, chartArea }) {
        const highlight = layer.options?.highlight
        if (![WeatherLayerDrawer.HIGHLIGHT_OPTIONS.point, WeatherLayerDrawer.HIGHLIGHT_OPTIONS.line, WeatherLayerDrawer.HIGHLIGHT_OPTIONS.bar, WeatherLayerDrawer.HIGHLIGHT_OPTIONS.background].includes(highlight)) return

        const data = layer.data || []
        const group = svg
            .append("g")
            .attr("class", `weather-layer-astro-highlight weather-layer-astro-highlight-${highlight}`)

        if (highlight === WeatherLayerDrawer.HIGHLIGHT_OPTIONS.background) {
            this.drawAstroBackgroundHighlight({ group, data, xAxis, margin, chartArea })
            group.lower()
            return
        }

        const markerData = data.flatMap(row => {
            return this.visibleAstroMarkers(row)
        })

        if (highlight === WeatherLayerDrawer.HIGHLIGHT_OPTIONS.point) {
            group
                .selectAll(".weather-layer-astro-highlight-point")
                .data(markerData)
                .join("circle")
                .attr("class", "weather-layer-astro-highlight-point")
                .attr("cx", row => xAxis.scale(parseCoordinate(row.time, xAxis.scaleType)) + margin.left)
                .attr("cy", chartArea.y + chartArea.height + 5)
                .attr("r", 4)
                .attr("fill", row => this.astroColorFor(row.type))
            return
        }

        group
            .selectAll(".weather-layer-astro-highlight-line")
            .data(markerData)
            .join("line")
            .attr("class", "weather-layer-astro-highlight-line")
            .attr("x1", row => xAxis.scale(parseCoordinate(row.time, xAxis.scaleType)) + margin.left)
            .attr("x2", row => xAxis.scale(parseCoordinate(row.time, xAxis.scaleType)) + margin.left)
            .attr("y1", chartArea.y)
            .attr("y2", chartArea.y + chartArea.height)
            .attr("stroke", row => this.astroColorFor(row.type))
            .attr("stroke-width", layer.options?.highlight_width || 2)
            .attr("stroke-dasharray", "4 4")
            .attr("opacity", layer.options?.highlight_opacity ?? 0.75)
            .attr("pointer-events", "none")
    }

    drawAstroBackgroundHighlight({ group, data, xAxis, margin, chartArea }) {
        data.forEach(row => {
            const sunriseX = xAxis.scale(parseCoordinate(this.sunriseFor(row), xAxis.scaleType)) + margin.left
            const sunsetX = xAxis.scale(parseCoordinate(this.sunsetFor(row), xAxis.scaleType)) + margin.left
            const startX = xAxis.scale(parseCoordinate(this.astroStartFor(row), xAxis.scaleType)) + margin.left
            const endX = xAxis.scale(parseCoordinate(this.astroEndFor(row), xAxis.scaleType)) + margin.left

            this.drawAstroBackgroundBand({ group, x: startX, endX: sunriseX, chartArea, color: "#1e3a8a", opacity: 0.1 })
            this.drawAstroBackgroundBand({ group, x: sunriseX, endX: sunsetX, chartArea, color: "#facc15", opacity: 0.12 })
            this.drawAstroBackgroundBand({ group, x: sunsetX, endX, chartArea, color: "#312e81", opacity: 0.1 })
        })
    }

    drawAstroBackgroundBand({ group, x, endX, chartArea, color, opacity }) {
        const clampedX = Math.max(chartArea.x, Math.min(x, chartArea.x + chartArea.width))
        const clampedEndX = Math.max(chartArea.x, Math.min(endX, chartArea.x + chartArea.width))
        if (!Number.isFinite(clampedX) || !Number.isFinite(clampedEndX) || clampedEndX <= clampedX) return

        group
            .append("rect")
            .attr("class", "weather-layer-astro-background-band")
            .attr("x", clampedX)
            .attr("y", chartArea.y)
            .attr("width", clampedEndX - clampedX)
            .attr("height", chartArea.height)
            .attr("fill", color)
            .attr("opacity", opacity)
            .attr("pointer-events", "none")
    }

    backgroundGradientIdFor({ defs, row, layer, data, backgroundColor, highlightColor }) {
        const color = highlightColor || this.highlightColorFor(row, layer, data)
        const gradientId = `weather-background-gradient-${this.weatherCodeFor(row)}-${this.timeFor(row)}`

        if (!defs.select(`#${gradientId}`).empty()) return gradientId

        const gradient = defs
            .append("linearGradient")
            .attr("id", gradientId)
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "0%")
            .attr("y2", "100%")

        gradient
            .append("stop")
            .attr("offset", "0%")
            .attr("stop-color", backgroundColor)
            .attr("stop-opacity", 1)

        gradient
            .append("stop")
            .attr("offset", "32%")
            .attr("stop-color", color)
            .attr("stop-opacity", layer.options?.highlight_opacity ?? 0.16)

        gradient
            .append("stop")
            .attr("offset", "100%")
            .attr("stop-color", color)
            .attr("stop-opacity", layer.options?.highlight_opacity ?? 0.16)

        return gradientId
    }

    highlightColorFor(row, layer, data) {
        if (layer.options?.highlight_color) return layer.options.highlight_color

        const temperatures = data
            .map(weatherRow => this.averageTempFor(weatherRow))
            .filter(Number.isFinite)
        const [minTemp, maxTemp] = d3.extent(temperatures)
        const temperature = this.averageTempFor(row)

        if (!Number.isFinite(temperature) || minTemp === maxTemp) return "#38bdf8"

        return d3.scaleLinear()
            .domain([
                minTemp,
                minTemp + ((maxTemp - minTemp) * 0.33),
                minTemp + ((maxTemp - minTemp) * 0.66),
                maxTemp
            ])
            .range(["#38bdf8", "#22c55e", "#f59e0b", "#ef4444"])
            .clamp(true)(temperature)
    }

    defsFor(svg) {
        let defs = svg.select("defs.line-chart-definitions")

        if (defs.empty()) {
            defs = svg
                .append("defs")
                .attr("class", "line-chart-definitions")
        }

        return defs
    }

    setHoverX(x) {
        this.hoverTargets.forEach(target => {
            d3.select(target.node)
                .attr("opacity", x >= target.start && x <= target.end ? 1 : 0)
        })
    }

    hideHover() {
        this.hoverTargets.forEach(target => d3.select(target.node).attr("opacity", 0))
    }

    async drawInlineIcon({ node, url, iconSize, className = "weather-layer-icon" }) {
        const icon = d3.select(node)
            .append("g")
            .attr("class", className)

        try {
            const document = await WeatherLayerDrawer.loadIcon(url)
            const svgNode = document.documentElement
            const viewBox = (svgNode.getAttribute("viewBox") || "0 0 30 30")
                .trim()
                .split(/\s+/)
                .map(Number)
            const [minX, minY, width, height] = viewBox
            const scale = iconSize / Math.max(width || 30, height || 30)
            const content = icon
                .append("g")
                .attr("transform", `translate(${-iconSize / 2}, ${-iconSize / 2}) scale(${scale}) translate(${-minX}, ${-minY})`)

            Array.from(svgNode.childNodes)
                .filter(child => child.nodeType === 1)
                .forEach(child => content.node().appendChild(child.cloneNode(true)))

            content
                .selectAll("path, polygon, circle, rect")
                .attr("fill", "currentColor")
        } catch {
            icon
                .append("text")
                .attr("x", -5)
                .attr("y", 5)
                .attr("font-size", 14)
                .attr("font-weight", 700)
                .attr("fill", "currentColor")
                .text("?")
        }
    }

    xPositionFor(row, xAxis, margin, { snapToData = false } = {}) {
        const x = xAxis.scale(parseCoordinate(this.timeFor(row), xAxis.scaleType)) + margin.left
        if (!snapToData) return x

        return this.nearestDataXPosition(x)
    }

    nearestDataXPosition(x, positions = this.dataXPositions || []) {
        if (positions.length === 0) return x

        return positions.reduce((nearest, position) => (
            Math.abs(position - x) < Math.abs(nearest - x) ? position : nearest
        ), positions[0])
    }

    coverageFor(row, index, data, xAxis, margin) {
        const explicitStart = this.startTimeFor(row)
        const explicitEnd = this.endTimeFor(row)
        if (explicitStart && explicitEnd) {
            const start = xAxis.scale(parseCoordinate(explicitStart, xAxis.scaleType)) + margin.left
            const end = xAxis.scale(parseCoordinate(explicitEnd, xAxis.scaleType)) + margin.left
            const plottedPositions = (this.dataXPositions || []).filter(x => x >= start && x <= end)
            if (plottedPositions.length > 0) {
                const plottedStart = d3.min(plottedPositions)
                const plottedEnd = d3.max(plottedPositions)

                return { start: plottedStart, end: plottedEnd, width: Math.max(plottedEnd - plottedStart, 0) }
            }

            return { start, end, width: Math.max(end - start, 0) }
        }

        const currentX = this.xPositionFor(row, xAxis, margin)
        const previousX = data[index - 1] ? this.xPositionFor(data[index - 1], xAxis, margin) : null
        const nextX = data[index + 1] ? this.xPositionFor(data[index + 1], xAxis, margin) : null
        const start = previousX === null ? currentX - ((nextX || currentX) - currentX) / 2 : (previousX + currentX) / 2
        const end = nextX === null ? currentX + (currentX - (previousX || currentX)) / 2 : (currentX + nextX) / 2

        return { start, end, width: Math.max(end - start, 0) }
    }

    timeFor(row) {
        return row[0]
    }

    startTimeFor(row) {
        return row[5]
    }

    endTimeFor(row) {
        return row[6]
    }

    visibleAstroMarkers(row) {
        return [
            { type: "sunrise", time: this.sunriseFor(row), row },
            { type: "sunset", time: this.sunsetFor(row), row }
        ].filter(marker => WeatherLayerDrawer.isAstroTimeInDomain(marker.time, this.xDomain, this.xScaleType))
    }

    iconUrlFor(row) {
        return row[4]
    }

    hourIconUrlFor(row) {
        return row[3]
    }

    astroIconUrlFor(row) {
        return row.type === "sunrise" ? row.row[4] : row.row[5]
    }

    titleFor(row) {
        const [, mintemp, maxtemp, code] = row
        return `${this.weatherDescriptionFor(row) || `Weather ${code}`}: ${mintemp}°C - ${maxtemp}°C`
    }

    minTempFor(row) {
        return row[1]
    }

    maxTempFor(row) {
        return row[2]
    }

    temperatureFor(row) {
        return row[1]
    }

    averageTempFor(row) {
        return (Number(this.minTempFor(row)) + Number(this.maxTempFor(row))) / 2
    }

    weatherCodeFor(row) {
        return row[3]
    }

    hourTitleFor(row) {
        const code = row[2]
        return `${this.weatherDescriptionFor(row) || `Weather ${code}`}: ${this.temperatureFor(row)}°C`
    }

    weatherDescriptionFor(row) {
        return row[7] || row[5]
    }

    sunriseFor(row) {
        return row[0]
    }

    sunsetFor(row) {
        return row[1]
    }

    astroStartFor(row) {
        return row[2]
    }

    astroEndFor(row) {
        return row[3]
    }

    astroColorFor(type) {
        return type === "sunrise" ? "#f59e0b" : "#7c3aed"
    }

    astroTitleFor(row) {
        return `${row.type === "sunrise" ? "Sunrise" : "Sunset"}: ${this.timeLabelFor(row.time)}`
    }

    timeLabelFor(time) {
        const date = parseCoordinate(time, "Time")
        if (this.timezone) return new Intl.DateTimeFormat("en-GB", {timeZone: this.timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23"}).format(date)

        return d3.timeFormat("%H:%M")(date)
    }
}
